/**
 * "Viewing as" — the company switcher, at the top of the left nav (platform admins only).
 *
 * Callum, 2026-09-24: "allow platform admins to be able to change the company they are
 * looking at… at the app top level… much like pricing and purchasing where you can toggle
 * the company in the left nav pane… irrelevant for employees cus they will always just see
 * the entity relevant to them."
 *
 * Same shape as isg-purchasing's `company-switcher.js`, deliberately:
 *
 * - 🔴 **The server decides who may switch.** `/me/companies` returns the list and
 *   `can_switch`; this file draws what it is told and re-derives nothing. The same rule
 *   gates the data (`scope.viewing_as`), so the two cannot disagree.
 * - **Every request carries `?company_id=<slug>`** while a company is chosen, and the
 *   server narrows the WHOLE scope to it — its entity and the divisions it sells — so
 *   every screen follows without knowing about the switcher.
 * - ⚠️ **The entity follows the company.** Pages pass `?entity=` from the filter bar; a uk
 *   entity sent while viewing as Sommer (de) would be refused, so the patch REWRITES any
 *   `entity=` to the chosen company's, and switching also resets the saved filter entity.
 * - ⚠️ `/me/companies` is exempt: scoping the switcher's own list by the override would
 *   let a bad value hide the means of correcting it. A stored company the server no
 *   longer offers is cleared on mount rather than left to 403 every call.
 * - Raw `fetch` callers (the two SSE streams) go through `withCompany()`.
 */
import { api, store, getIcon, showModal } from '@isg/ui-kit';

const STORAGE_KEY = 'isg-sales.viewingAs';
const NAV_ID = 'company-switcher-nav';
const FILTER_KEY = 'si_filters';
const ENTITY_LABEL = { uk: 'United Kingdom', us: 'United States', de: 'Germany' };
let actAsCache = null;

async function fetchActAs() {
  if (actAsCache) return actAsCache;
  try {
    const r = await api.get('/sales/me/act-as');
    const d = (r && r.data) || r || {};
    actAsCache = { items: d.items || [], scope: d.scope || null };
  } catch {
    actAsCache = { items: [], scope: null };     // most people: not offered, never an error
  }
  return actAsCache;
}

let scopeCache = null;

export function getViewingAs() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
}

function setViewingAs(company) {
  try {
    if (company) localStorage.setItem(STORAGE_KEY, JSON.stringify({ slug: company.slug, entity: company.entity_code, name: company.name }));
    else localStorage.removeItem(STORAGE_KEY);
    // top-down (a company) and drill-down (a person) are either/or — picking one ends the other
    if (company) sessionStorage.removeItem(ACT_KEY);
    // the filter bar's saved entity and division belong to the previous company
    const raw = sessionStorage.getItem(FILTER_KEY);
    const f = raw ? JSON.parse(raw) : {};
    delete f.division;
    if (company) f.entity = company.entity_code; else delete f.entity;
    sessionStorage.setItem(FILTER_KEY, JSON.stringify(f));
  } catch { /* storage unavailable — the choice just won't survive a reload */ }
}

function exempt(path) {
  return path.startsWith('/auth/') || path.startsWith('/api/auth/') || path.includes('/me/companies')
    || path.includes('/me/act-as') || path.includes('/preferences')
    || path.includes('/user/profile') || path.includes('/branding');
}

/** A URL with the chosen company applied: `company_id` added, any `entity=` rewritten. */
// ── Acting as a person (platform admins, for demos — Callum, 2026-09-24) ─────
// Every call carries `as_user`, and the SERVER builds that person's scope and makes it
// read-only (app/platform/scope.py → acting_as). It replaces the company choice.
const ACT_KEY = 'isg-sales.actingAs';

/** Stop acting as someone and reload THIS page as yourself (writes come back). */
export function stopActingAs() {
  setActingAs(null);
  window.location.reload();
}

export function getActingAs() {
  try { return JSON.parse(sessionStorage.getItem(ACT_KEY) || 'null'); } catch { return null; }
}

function setActingAs(person) {
  try {
    if (person) sessionStorage.setItem(ACT_KEY, JSON.stringify({ id: person.id, name: person.name }));
    else sessionStorage.removeItem(ACT_KEY);
    if (person) localStorage.removeItem(STORAGE_KEY);   // drilling into a person ends the company view
    const raw = sessionStorage.getItem(FILTER_KEY);
    const f = raw ? JSON.parse(raw) : {};
    delete f.division; delete f.entity;             // their entity, not the admin's last one
    sessionStorage.setItem(FILTER_KEY, JSON.stringify(f));
  } catch { /* storage unavailable */ }
}

export function withCompany(path) {
  const who = getActingAs();
  if (who && typeof path === 'string' && !exempt(path) && !/[?&]as_user=/.test(path)) {
    return `${path}${path.includes('?') ? '&' : '?'}as_user=${encodeURIComponent(who.id)}`;
  }
  const v = getViewingAs();
  if (!v || typeof path !== 'string' || exempt(path) || /[?&]company_id=/.test(path)) return path;
  let out = path.replace(/([?&])entity=[^&]*/g, `$1entity=${encodeURIComponent(v.entity)}`);
  out += `${out.includes('?') ? '&' : '?'}company_id=${encodeURIComponent(v.slug)}`;
  return out;
}

/** Call once at boot, before any page fetches. */
export function patchApiForViewingAs() {
  if (api.__viewingAsPatched) return;
  api.__viewingAsPatched = true;
  const original = api.request.bind(api);
  api.request = (path, options = {}) => original(withCompany(path), options);
}

async function fetchScope() {
  if (scopeCache) return scopeCache;
  try {
    const r = await api.get('/sales/me/companies');
    const d = (r && r.data) || r || {};
    scopeCache = { items: d.items || [], can_switch: !!d.can_switch };
  } catch {
    scopeCache = { items: [], can_switch: false };   // never break the app over a switcher
  }
  return scopeCache;
}

async function render() {
  document.getElementById(NAV_ID)?.remove();
  const navHost = document.querySelector('.sidebar .sidebar-nav');
  if (!navHost) return;
  const scope = await fetchScope();
  const current = getViewingAs();
  if (current && !scope.items.some(c => c.slug === current.slug)) {
    setViewingAs(null);
    window.location.reload();
    return;
  }
  // "Act as": platform admins (anyone) and managers (their own team). A stale choice for
  // somebody no longer in that list is dropped rather than failing every call.
  const people = await fetchActAs();
  const acting = getActingAs();
  if (acting && !people.items.some(p => p.id === acting.id)) {
    setActingAs(null);
    window.location.reload();
    return;
  }
  if (people.items.length || acting) renderActing(navHost, people);
  if (!scope.can_switch) return;

  const label = current ? current.name : 'All companies';
  const sub = current ? (ENTITY_LABEL[current.entity] || current.entity.toUpperCase()) : 'uk · us · de';
  const section = document.createElement('div');
  section.className = 'nav-section';
  section.id = NAV_ID;
  section.setAttribute('role', 'group');
  section.setAttribute('aria-label', 'Viewing as');
  // A narrowed view must be impossible to miss: every number in the app is one company's.
  const activeStyle = current ? 'background:var(--brand-800);border-left:2px solid var(--accent-500);' : '';
  const dot = current ? 'var(--accent-500)' : '#10b981';
  section.innerHTML = `
    <div class="nav-section-label" aria-hidden="true">Viewing as</div>
    <button type="button" class="nav-item" id="cs-trigger" style="${activeStyle}" aria-haspopup="dialog"
            title="${current ? `Showing only ${esc(label)} — click to change` : 'Showing every company — click to pick one'}">
      <span class="nav-item-icon" aria-hidden="true" style="display:inline-flex;align-items:center;justify-content:center">
        <span style="width:8px;height:8px;border-radius:50%;background:${dot};display:inline-block"></span>
      </span>
      <span class="nav-item-label" style="display:flex;flex-direction:column;line-height:1.25;min-width:0">
        <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(label)}</span>
        <span style="font-size:0.625rem;text-transform:uppercase;letter-spacing:0.04em;opacity:0.7">${esc(sub)}</span>
      </span>
      <span aria-hidden="true" style="margin-left:auto;opacity:0.6">${getIcon('chevron-down', 14)}</span>
    </button>`;
  navHost.insertBefore(section, navHost.firstChild);
  section.querySelector('#cs-trigger').addEventListener('click', openModal);
}

async function openModal() {
  const scope = await fetchScope();
  const current = getViewingAs();
  const content = document.createElement('div');
  content.innerHTML = `
    <style>.cs-row:hover{background:var(--gray-100,#f3f4f6) !important;}</style>
    <p style="margin:0 0 var(--space-3);font-size:var(--text-sm);color:var(--text-secondary);line-height:1.5">
      Shows the whole app as <strong>one company</strong> sees it — its country and the divisions it sells.
      Only platform admins have this; everybody else always sees their own.</p>
    <input id="cs-filter" class="form-input" type="search" placeholder="Filter companies…" style="margin-bottom:var(--space-3)" autocomplete="off">
    <div id="cs-list" style="max-height:50vh;overflow-y:auto;display:flex;flex-direction:column;gap:2px"></div>
    <div style="border-top:1px solid var(--border);margin-top:var(--space-3);padding-top:var(--space-3)">
      <button id="cs-clear" type="button" class="btn btn-ghost btn-sm" style="width:100%;justify-content:flex-start" ${current ? '' : 'disabled'}>
        ${getIcon('refresh-cw', 14)} Show every company</button>
    </div>`;
  showModal({ title: 'Viewing as', content, size: 'sm' });
  const listEl = content.querySelector('#cs-list');
  content.querySelector('#cs-clear').addEventListener('click', () => { setViewingAs(null); window.location.reload(); });
  const draw = (q = '') => {
    const n = q.trim().toLowerCase();
    const rows = scope.items.filter(c => !n || (c.name || '').toLowerCase().includes(n) || c.slug.includes(n) || c.entity_code.includes(n));
    let last = null;
    listEl.innerHTML = rows.length ? rows.map(c => {
      const head = c.entity_code !== last ? `<div style="font-size:var(--text-xs);font-weight:600;text-transform:uppercase;letter-spacing:.04em;color:var(--text-tertiary);padding:var(--space-2) var(--space-3) 2px">${esc(ENTITY_LABEL[c.entity_code] || c.entity_code)}</div>` : '';
      last = c.entity_code;
      return `${head}<button type="button" class="cs-row" data-slug="${esc(c.slug)}"
        style="width:100%;text-align:left;padding:var(--space-2) var(--space-3);background:${current && c.slug === current.slug ? 'var(--gray-100,#f3f4f6)' : 'transparent'};border:0;border-radius:var(--radius);cursor:pointer;display:flex;justify-content:space-between;align-items:center;gap:var(--space-2);color:var(--text-primary)">
        <span style="min-width:0"><strong style="display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(c.name)}</strong>
          <span style="color:var(--text-tertiary);font-size:var(--text-xs)">${c.divisions.length} division${c.divisions.length === 1 ? '' : 's'}: ${esc(c.divisions.join(', '))}</span></span>
        ${current && c.slug === current.slug ? '<span style="color:var(--accent-500,#BD1B44);flex-shrink:0">●</span>' : ''}
      </button>`;
    }).join('') : `<div style="padding:var(--space-3);color:var(--text-tertiary)">${scope.items.length ? 'No matches.' : 'No companies available.'}</div>`;
    listEl.querySelectorAll('button[data-slug]').forEach(b => b.addEventListener('click', () => {
      setViewingAs(scope.items.find(c => c.slug === b.dataset.slug));
      window.location.reload();
    }));
  };
  draw();
  const f = content.querySelector('#cs-filter');
  f.addEventListener('input', e => draw(e.target.value));
  setTimeout(() => f.focus(), 50);
}

/** Mount the control; re-render whenever auth state lands or the shell is rebuilt. */
export function mountCompanySwitcher() {
  if (store.get('user')) render();
  if (!mountCompanySwitcher._sub) {
    mountCompanySwitcher._sub = true;
    store.subscribe('user', () => render());
  }
}

function esc(s) {
  return s == null ? '' : String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}


// ── the "Act as" nav entry, banner and picker ────────────────────────────────

function renderActing(navHost, people = { items: [], scope: null }) {
  document.getElementById('act-as-nav')?.remove();
  document.getElementById('act-as-banner')?.remove();
  const who = getActingAs();
  const section = document.createElement('div');
  section.className = 'nav-section';
  section.id = 'act-as-nav';
  section.innerHTML = `
    <div class="nav-section-label" aria-hidden="true">Act as</div>
    <button type="button" class="nav-item" id="aa-trigger" style="${who ? 'background:var(--brand-800);border-left:2px solid var(--accent-500);' : ''}"
            title="${who ? `Seeing the app as ${esc(who.name)} — click to change or stop` : 'See the app as a rep sees it (read-only), e.g. for a demo'}">
      <span class="nav-item-icon" aria-hidden="true">${getIcon(who ? 'eye' : 'user', 16)}</span>
      <span class="nav-item-label" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(who ? who.name : 'Yourself')}</span>
      <span aria-hidden="true" style="margin-left:auto;opacity:0.6">${getIcon('chevron-down', 14)}</span>
    </button>`;
  navHost.insertBefore(section, navHost.firstChild);
  section.querySelector('#aa-trigger').addEventListener('click', openActModal);
  if (who) {
    // impossible to miss: every screen is somebody else's, and nothing can be saved
    const bar = document.createElement('div');
    bar.id = 'act-as-banner';
    bar.style.cssText = 'position:sticky;top:0;z-index:60;background:var(--accent-500,#BD1B44);color:#fff;padding:6px 16px;font-size:13px;display:flex;gap:12px;align-items:center;justify-content:center';
    bar.innerHTML = `${getIcon('eye', 14)} <span>Acting as <strong>${esc(who.name)}</strong> — you are seeing their day, read-only. Nothing you do is saved as them.</span>
      <button type="button" class="btn btn-sm" style="background:rgba(255,255,255,.2);color:#fff;border:1px solid rgba(255,255,255,.4)" id="aa-stop">Stop</button>`;
    const main = document.querySelector('.main-content') || document.body;
    main.insertBefore(bar, main.firstChild);
    bar.querySelector('#aa-stop').addEventListener('click', () => { setActingAs(null); window.location.assign('/'); });
  }
}

async function openActModal() {
  const who = getActingAs();
  const list = await fetchActAs();
  const content = document.createElement('div');
  content.innerHTML = `
    <p style="margin:0 0 var(--space-3);font-size:var(--text-sm);color:var(--text-secondary);line-height:1.5">
      See the app <strong>as that person sees it</strong> — their Today, their book, their tasks and meetings.
      Read-only: nothing can be saved as them. ${list.scope === 'my_team'
        ? 'You can pick the people in your own team, and you see no more of their accounts than your own access allows.'
        : 'People without Sales access are shown from their confirmed rep codes.'}</p>
    <input id="aa-filter" class="form-input" type="search" placeholder="Filter people…" style="margin-bottom:var(--space-3)" autocomplete="off">
    <div id="aa-list" style="max-height:50vh;overflow-y:auto;display:flex;flex-direction:column;gap:2px"><div class="spinner" style="margin:12px auto"></div></div>
    <div style="border-top:1px solid var(--border);margin-top:var(--space-3);padding-top:var(--space-3)">
      <button id="aa-clear" type="button" class="btn btn-ghost btn-sm" style="width:100%;justify-content:flex-start" ${who ? '' : 'disabled'}>
        ${getIcon('refresh-cw', 14)} Back to yourself</button></div>`;
  showModal({ title: 'Act as', content, size: 'sm' });
  content.querySelector('#aa-clear').addEventListener('click', () => { setActingAs(null); window.location.assign('/'); });
  const people = list.items;
  const listEl = content.querySelector('#aa-list');
  const draw = (q = '') => {
    const n = q.trim().toLowerCase();
    const rows = people.filter(p => !n || (p.name || '').toLowerCase().includes(n) || (p.email || '').toLowerCase().includes(n));
    listEl.innerHTML = rows.length ? rows.map(p => `<button type="button" class="cs-row" data-id="${p.id}"
        style="width:100%;text-align:left;padding:var(--space-2) var(--space-3);background:${who && who.id === p.id ? 'var(--gray-100,#f3f4f6)' : 'transparent'};border:0;border-radius:var(--radius);cursor:pointer;color:var(--text-primary)">
        <strong style="display:block">${esc(p.name)}</strong>
        <span style="color:var(--text-tertiary);font-size:var(--text-xs)">${esc(p.job_title || p.email || '')}${p.codes ? ` · ${p.codes} rep code${p.codes === 1 ? '' : 's'}` : ''}${p.has_sales ? '' : ' · no Sales access'}</span>
      </button>`).join('') : '<div style="padding:var(--space-3);color:var(--text-tertiary)">No matches.</div>';
    listEl.querySelectorAll('button[data-id]').forEach(b => b.addEventListener('click', () => {
      const p = people.find(x => x.id === Number(b.dataset.id));
      setActingAs(p);
      window.location.assign('/today');
    }));
  };
  draw();
  const f = content.querySelector('#aa-filter');
  f.addEventListener('input', e => draw(e.target.value));
  setTimeout(() => f.focus(), 50);
}
